import { LOCALES } from "@/enums/locales";
import i18n from "../../i18n";
import Vue from "vue";
import { PROFILES_ENUM } from "@/enums/profiles/profiles";
import { FINANCIAL_IDS } from "@/enums/financials/financialIds";
import { resolveNotaryTransferFeeValue } from "@/utils/simulation/financialUtils";

const locale = localStorage.getItem("locale");

if (locale) {
    i18n.locale = LOCALES[locale];
} else {
    i18n.locale = LOCALES["pt_pt"];
}

const launchErrorToast = (msg) => {
    Vue.$toast.open({
        message: msg,
        dismissible: true,
        type: "error",
    });
};

const clientInfoValidation = (state, hasError) => {
    if (state.clientInfo?.birthdateError) {
        launchErrorToast(
            i18n.t("pages.SimulatorPage.simulationError.clientInfoError.birthdate")
        );
        hasError = true;
    }
    if (state.clientInfo?.email && testIfItIsNotEmail(state.clientInfo?.email)) {
        launchErrorToast(
            i18n.t("pages.SimulatorPage.simulationError.clientInfoError.email")
        );
        hasError = true;
    }
    return hasError;
}

const vehicleInfoValidation = (state, hasError) => {
    var isVehicleUsed =
        state.vehicleInfo.vehicleCondition?.id === 2 ? true : false;
    const hasPlateYear = state.vehicleInfo.plateYear?.value === -1 ? false : true;
    const hasPlateMonth =
        state.vehicleInfo.plateMonth?.value === -1 ? false : true;

    const checks = [
        {
            check: !state.vehicleInfo?.version && isVehicleUsed,
            message: i18n.t("pages.SimulatorPage.simulationError.vehicleInfoError.data"),
        },
        {
            check: !state.vehicleInfo?.vehicleType && isVehicleUsed,
            message: i18n.t("pages.SimulatorPage.simulationError.vehicleInfoError.type"),
        },
        {
            check: !state.vehicleInfo?.brand && isVehicleUsed,
            message: i18n.t("pages.SimulatorPage.simulationError.vehicleInfoError.brand"),
        },
        {
            check: !state.vehicleInfo?.model && isVehicleUsed,
            message: i18n.t("pages.SimulatorPage.simulationError.vehicleInfoError.model"),
        },
        {
            check: (!hasPlateYear || !hasPlateMonth) && isVehicleUsed,
            message: i18n.t(`pages.SimulatorPage.simulationError.vehicleInfoError.plate`),
        },
    ];

    checks.forEach(({ check, message }) => {
        if (check) {
            launchErrorToast(message);
            hasError = true;
        }
    });

    return hasError;
}

const finInfoValidation = (state, hasError, fin, product, isBBVASpain) => {
    const checks = [
        {
            check: fin === -1,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.noFin"),
        },
        {
            check: product === -1,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.product"),
        },
        {
            // https://dtogsc.atlassian.net/browse/SIFA-1581
            // Simulador - para o produto Hyundai Open Drive - é obrigatório registar o valor residual, mesmo que a viatura seja nova
            check: (product === 15 || product === "15") && (!state.financingInfo?.residualValue || state.financingInfo?.residualValue === "0"),
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.residualValueHyundaiOpenDrive"),
        },
        {
            check: !isBBVASpain && state.financingInfo?.ppcValue?.value !== -1 && state.financingInfo?.ppcNrValue?.value === 1,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.nrInsured"),
        },
        {
            check: !isBBVASpain && state.financingInfo?.ppcValue?.value === -1 && state.financingInfo?.ppcNrValue?.value !== 1,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.nrValue"),
        },
        {
            check: !state.financingInfo?.pvp,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.pvp"),
        },
        {
            check: (state.financingInfo?.expenses?.length === 0) && !isBBVASpain,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.expenses"),
        },
        {
            check: (state.financingInfo?.legalTax?.length === 0) && !isBBVASpain,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.legalTax"),
        },
        {
            check: !state.financingInfo?.period,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.nrMonths"),
        },
        {
            check: !state.financingInfo?.rate && !isBBVASpain,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.rate"),
        },
        {
            check: (fin === "4" || fin === 4) && state.financingInfo?.interestRate?.id === 2 && state.clientInfo?.clientType === 1,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.interestRateSantander"),
        },
        {
            check: (fin === "19" || fin === 19) && (state.financingInfo?.notaryTransferFeesBillTheValue?.value == '' || state.financingInfo?.notaryTransferFeesBillTheValue?.value == '-1'),
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.notaryTransferFeesBillTheValue"),
        },
    ];

    checks.forEach(({ check, message }) => {
        if (check) {
            launchErrorToast(message);
            hasError = true;
        }
    });

    return hasError;
}

const bbvaSpainValidation = (state, hasError, isBBVASpain, fin) => {
    const ppcValueIdToNumber = Number(state.financingInfo?.ppcValue?.value);
    const ppcValueText = state.financingInfo?.ppcValue?.text;
    const birthdate = state.clientInfo?.birthdate;
    let age = 0;
    let annees = 0;
    let avoirPlusDe79ans = false;

    if (birthdate) {
        const birthdateObj = new Date(birthdate);
        const today = new Date();

        age = today.getFullYear() - birthdateObj.getFullYear();

        if (
            today.getMonth() < birthdateObj.getMonth() ||
            (today.getMonth() === birthdateObj.getMonth() && today.getDate() < birthdateObj.getDate())
        ) {
            age--;
        }
    }

    if (state.financingInfo?.period) {
        const period = Number(state.financingInfo?.period);
        // Calcul des années et des mois
        annees = Math.floor(period / 12);
        avoirPlusDe79ans = (annees + age) > 79;
    }

    const checks = [
        {
            check: isBBVASpain && state.financingInfo?.period === -1,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.nrMonths"),
        },
        {
            check: isBBVASpain && state.financingInfo?.rate === -1,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.tin"),
        },
        // Contabiliza o ppc com id 25 - Sin seguro - valida se o nr insured é 0
        {
            check: isBBVASpain && (state.financingInfo?.ppcNrValue?.value === 1 && ppcValueIdToNumber !== 25),
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.nrInsured"),
        },
        {
            check: isBBVASpain && (state.financingInfo?.ppcNrValue?.value !== 1 && ppcValueIdToNumber === 25),
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.zeroNrInsured"),
        },
        {
            check: isBBVASpain && state.financingInfo?.ppcValue?.value === -1,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.nrValue"),
        },
        {
            check: isBBVASpain && ppcValueText === "PPP" && age > 65,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.pppValidation"),
        },
        {
            check: ppcValueText === "Vida Senior" && (age < 65 || age > 75) && age !== 0,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.seniorPPCValidation"),
        },
        {
            // Apenas BBVA ES e maior do que 79 + Periodo
            check: avoirPlusDe79ans && fin === 12,
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.bbvaEsMoreThan79YearsOld"),
        }
    ];

    checks.forEach(({ check, message }) => {
        if (check) {
            launchErrorToast(message);
            hasError = true;
        }
    });
    return hasError;
}

const profileValidation = (state, hasError, userProfileId, fin, product) => {
    const sellerProfile = userProfileId === PROFILES_ENUM.SELLER;
    const businessBossProfile = userProfileId === PROFILES_ENUM.BUSINESS_BOSS;
    const isCompanyType = state.clientInfo?.clientType === 3;
    const numberFin = Number(fin);
    const numberProduct = Number(product);

    // Seller Profile
    const checks = [
        {
            check: !state.vehicleInfo?.version && (sellerProfile || businessBossProfile),
            message: i18n.t("pages.SimulatorPage.simulationError.vehicleInfoError.data"),
        },
        {
            // quando não é company ou não é credibom ALD ou Leasing
            check: state.financingInfo?.ppcValue?.value === -1 && sellerProfile && !isCompanyType && !(numberFin === 9 && (numberProduct === 2 || numberProduct === 3)),
            message: i18n.t("pages.SimulatorPage.simulationError.financingInfoError.nrValue"),
        },
    ];

    checks.forEach(({ check, message }) => {
        if (check) {
            launchErrorToast(message);
            hasError = true;
        }
    });

    return hasError;
}

const throwToastError = (isBBVASpain, state, product, fin, userProfileId) => {
    var hasError = false;
    var vehicleError = false;

    Vue.$toast.clear();

    // Vehicle Info
    vehicleError = vehicleInfoValidation(state, hasError);

    // Financial Info
    hasError = finInfoValidation(state, hasError, fin, product, isBBVASpain);

    // Client Info
    hasError = clientInfoValidation(state, hasError);

    // BBVA Validation
    hasError = bbvaSpainValidation(state, hasError, isBBVASpain, fin);

    // Profile Validation
    hasError = profileValidation(state, hasError, userProfileId, fin, product);

    console.log(state)

    return { hasError: hasError, vehicleError: vehicleError };
}

const validateUserInputs = (product, fin, state, userProfileId) => {

    const isBBVASpain = state.bbvaSpain;
    const sellerProfile = userProfileId === PROFILES_ENUM.SELLER;
    const businessBossProfile = userProfileId === PROFILES_ENUM.BUSINESS_BOSS;
    const isCompanyType = state.clientInfo?.clientType === 3;

    const hasVehicleBorderSeller = !state.vehicleInfo?.version && sellerProfile;
    const hasVehicleBorderBusinessBoss = !state.vehicleInfo?.version && businessBossProfile;
    const numberFin = Number(fin);
    const numberProduct = Number(product);

    let { hasError, vehicleError } = throwToastError(isBBVASpain, state, product, fin, userProfileId);

    //Vehicle Border Validation
    state.validationBorder["vehicle"] = hasVehicleBorderSeller || vehicleError || hasVehicleBorderBusinessBoss;

    //Financial Border Validation
    state.validationBorder["expenses"] = (state.financingInfo?.expenses?.length === 0);
    state.validationBorder["period"] = !state.financingInfo?.period;
    state.validationBorder["pvp"] = !state.financingInfo?.pvp;
    state.validationBorder["rate"] = !state.financingInfo?.rate;
    state.validationBorder["legalTax"] = (state.financingInfo?.legalTax?.length === 0);
    // state.validationBorder["nrPPCInsuredPeople"] =
    //     state.financingInfo?.ppcValue?.value !== -1 &&
    //     state.financingInfo?.ppcNrValue?.value === 1;
    state.validationBorder["fin"] = fin === -1;
    state.validationBorder["prod"] = product === -1;

    //BBVA Validation //Seller profile Validation
    state.validationBorder["ppcName"] = isBBVASpain || sellerProfile ?
        state.financingInfo?.ppcValue?.value === -1 : false;

    if(Number(fin) === FINANCIAL_IDS.COFIDIS_PT) {
        state.validationBorder["notaryTransferFees"] = (state.financingInfo?.notaryTransferFeesBillTheValue?.value === '' || state.financingInfo?.notaryTransferFeesBillTheValue?.value == '-1');
    }

    const ppcValueIdToNumber = Number(state.financingInfo?.ppcValue?.value);

    if (isBBVASpain || (sellerProfile && !isCompanyType && !(numberFin === 9 && (numberProduct === 2 || numberProduct === 3)))) {
        state.validationBorder["ppcName"] = state.financingInfo?.ppcValue?.value === -1;
        // Contabiliza o ppc com id 25 - Sin seguro (Sofinco) - valida se o nr insured é 0
        state.validationBorder["nrPPCInsuredPeople"] = (state.financingInfo?.ppcNrValue?.value === 1 && ppcValueIdToNumber !== 25) || (state.financingInfo?.ppcNrValue?.value !== 1 && ppcValueIdToNumber === 25);
    } else {
        state.validationBorder["ppcName"] = state.financingInfo?.ppcValue?.value === -1 && state.financingInfo?.ppcNrValue?.value !== 1;
        state.validationBorder["nrPPCInsuredPeople"] = state.financingInfo?.ppcValue?.value !== -1 && state.financingInfo?.ppcNrValue?.value === 1;
    }

    state.validationBorder["periodBBVA"] =
        isBBVASpain && state.financingInfo?.period === -1;

    state.validationBorder["tin"] =
        isBBVASpain && state.financingInfo?.rate === -1;

    const birthdate = state.clientInfo?.birthdate;
    const ppcValueText = state.financingInfo?.ppcValue?.text;
    let age = 0;
    let annees = 0;
    let avoirPlusDe79ans = false;

    if (birthdate) {
        const birthdateObj = new Date(birthdate);
        const today = new Date();

        age = today.getFullYear() - birthdateObj.getFullYear();

        if (
            today.getMonth() < birthdateObj.getMonth() ||
            (today.getMonth() === birthdateObj.getMonth() && today.getDate() < birthdateObj.getDate())
        ) {
            age--;
        }
    }

    if (state.financingInfo?.period) {
        const period = Number(state.financingInfo?.period);
        // Calcul des années et des mois
        annees = Math.floor(period / 12);
        avoirPlusDe79ans = (annees + age) > 79;
    }

    //BBVA Validation
    state.validationBorder["ppcName"] = isBBVASpain && ppcValueText === "PPP" && age > 65;
    //Credibom - Seguro Vida Sénior Validação: Só pode deixar avançar se o cliente tiver idade para tal (Validações Simulação & Proposta)
    state.validationBorder["birthdate"] = ppcValueText === "Vida Senior" && (age < 65 || age > 75) && age !== 0;

    // Validador de email
    // https://dtogsc.atlassian.net/browse/SIFA-1489
    // Garantir que o email tem sempre um @ - caso contrário dar erro.
    state.clientInfo["emailValidationBorder"] = state.clientInfo?.email && testIfItIsNotEmail(state.clientInfo?.email);

    // https://dtogsc.atlassian.net/browse/SIFA-1581
    // Simulador - para o produto Hyundai Open Drive - é obrigatório registar o valor residual, mesmo que a viatura seja nova
    state.validationBorder["residualValue"] =
        (product === 15 || product === "15") && (!state.financingInfo?.residualValue || state.financingInfo?.residualValue === "0");

    state.validationBorder["rateType"] = (fin === "4" || fin === 4) && state.financingInfo?.interestRate?.id === 2 && state.clientInfo?.clientType === 1;

    state.validationBorder["birthdate"] = avoirPlusDe79ans && fin === 12;
    state.validationBorder["periodBBVA"] = avoirPlusDe79ans && fin === 12;

    hasError = vehicleError ? vehicleError : hasError;

    return hasError;
};

const validateSellersTaxPayload = (state, userProfileId, rootState) => {
    const sellerProfile = userProfileId === PROFILES_ENUM.SELLER;
    const savedTaxPayload = state.sellersStuff.payloadClone;
    const instalationId =
        rootState.AuthenticationModule.instalationsDropdown.find(
            (item) => item.selected
        )?.value;
    const selectedEntity = rootState.AuthenticationModule.rawInstalations.find(
        item => Number(item.ID) === Number(instalationId)
    );
    const isVehicleUsed =
        state.vehicleInfo.vehicleCondition?.id === 2 ? true : false;
    const idProduct = state.financingInfo.selectedProd?.value;
    const idSelFinancial = state.financingInfo.selectedFin?.value;
    const ppc = state.financingInfo.ppcValue;
    const ppcNumber = state.financingInfo.ppcNrValue;
    const legalTaxInvoicedTo = resolveNotaryTransferFeeValue(
        idSelFinancial,
        state.financingInfo?.notaryTransferFeesBillTheValue?.value,
        selectedEntity?.ID_ENTITYCOMPANY
    );

    if (!sellerProfile) return true;

    const payload = {
        "idEntity": Number(instalationId),
        "cartype": state.vehicleInfo.vehicleCondition?.id === 1
            ? "Novo"
            : "Usado",
        "eurotaxbrand": state.vehicleInfo?.brand,
        ...(isVehicleUsed && { caryear: Number(state.vehicleInfo?.plateYear?.text) }),
        "product": Number(idProduct),
        "ratetype": state.financingInfo?.interestRate?.id === 1 ? "Fixa" : "Indexada",
        "nrpayments": Number(state.financingInfo?.period),
        "financial": Number(idSelFinancial),
        "salesmantable": state.sellersStuff?.payloadClone?.salesmantable,
        "clienttype": state.clientInfo?.clientType,
        "initialvalueeur": Number(state.financingInfo?.initialEntry),
        "periodicity": state.financingInfo?.periodicity?.id === 1 ? "Mensal" : "Trimestral",
        "pvp": Number(state.financingInfo?.pvp),
        "expenses": Number(state.financingInfo?.expenses),
        "expensesFinance": state.financingInfo?.withFinancedExpenses.id === 1
            ? "S"
            : "N",
        "taxStampFinance": state.financingInfo?.withFinancedTax.id === 1
            ? "S"
            : "N",
        "ppc": ppc.value === -1 ? "" : ppc.text,
        "legalTaxInvoicedTo": legalTaxInvoicedTo,
        "insurance_ppcnrinsuredpeople": ppcNumber.value == -1 ? 0 : Number(ppcNumber.text)
    };

    const keys1 = Object.keys(savedTaxPayload);
    const keys2 = Object.keys(payload);

    console.log(savedTaxPayload, payload)

    if (keys1.length !== keys2.length) {
        return false;
    } else {
        return keys1.every(key => savedTaxPayload[key] === payload[key]);
    }
};

const validateInputsBeforeFetchSellersTax = (state) => {
    Vue.$toast.clear();

    var hasError = false;
    const product = state.financingInfo.selectedProd?.value;
    const fin = state.financingInfo.selectedFin?.value;
    var isVehicleUsed =
        state.vehicleInfo.vehicleCondition?.id === 2 ? true : false;
    const hasPlateYear = state.vehicleInfo.plateYear?.value === -1 ? false : true;
    const hasPlateMonth =
        state.vehicleInfo.plateMonth?.value === -1 ? false : true;

    if ((!hasPlateYear || !hasPlateMonth) && isVehicleUsed) {
        launchErrorToast(
            i18n.t(`pages.SimulatorPage.simulationError.vehicleInfoError.plate`)
        );
        hasError = true;
    }

    if (!state.financingInfo?.pvp) {
        launchErrorToast(
            i18n.t("pages.SimulatorPage.simulationError.financingInfoError.pvp")
        );
        hasError = true;

    }

    if (!state.financingInfo?.period) {
        launchErrorToast(
            i18n.t("pages.SimulatorPage.simulationError.financingInfoError.nrMonths")
        );
        hasError = true;
    }

    if (!state.financingInfo?.expenses) {
        launchErrorToast(
            i18n.t("pages.SimulatorPage.simulationError.financingInfoError.expenses")
        );
        hasError = true;
    }

    if (fin === -1) {
        launchErrorToast(
            i18n.t("pages.SimulatorPage.simulationError.financingInfoError.noFin")
        );
        hasError = true;
    }

    if (product === -1) {
        launchErrorToast(
            i18n.t("pages.SimulatorPage.simulationError.financingInfoError.product")
        );
        hasError = true;
    }

    if (!state.vehicleInfo?.version) {
        launchErrorToast(
            i18n.t("pages.SimulatorPage.simulationError.vehicleInfoError.brand")
        );
        hasError = true;
    }

    state.validationBorder["pvp"] = !state.financingInfo?.pvp;
    state.validationBorder["period"] = !state.financingInfo?.period;
    state.validationBorder["expenses"] = (state.financingInfo?.expenses.length === 0);
    state.validationBorder["fin"] = fin === -1;
    state.validationBorder["prod"] = product === -1;
    state.validationBorder["vehicle"] = !state.vehicleInfo?.version || ((!hasPlateYear || !hasPlateMonth) && isVehicleUsed);

    return hasError;
};

function testIfItIsNotEmail(clientEmail) {
    // NOSONAR return !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(clientEmail);
    return !/^[\w.-]+@[\w.-]+\.\w{2,}$/.test(clientEmail);
}

export { validateUserInputs, validateSellersTaxPayload, validateInputsBeforeFetchSellersTax };


